import React, { useState, useEffect, useCallback } from 'react';

const ItineraryAccordion = ({ day, title, description, transferFrom, transferStop, transferTo, checkInTime, checkOutTime, hotels, inclusions, activity }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalImages, setModalImages] = useState([]);

    const openModal = (index, images) => {
        const hotelImages = (hotels || [])
            .filter(hotel => hotel.image?.trim())
            .map(hotel => ({ url: hotel.image, alt: hotel.name?.trim() || 'Hotel' }));
        const activityImage = activity && activity.image?.trim() ? [{ url: activity.image, alt: activity.name?.trim() || 'Activity' }] : [];
        const combinedImages = [...hotelImages, ...activityImage];
        if (combinedImages.length === 0) return;
        setModalImages(combinedImages);
        setCurrentImageIndex(index < combinedImages.length ? index : 0);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalImages([]);
        setCurrentImageIndex(0);
    };

    const prevImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === 0 ? modalImages.length - 1 : prev - 1));
    }, [modalImages.length]);

    const nextImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === modalImages.length - 1 ? 0 : prev + 1));
    }, [modalImages.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isModalOpen) return;
            if (e.key === 'ArrowLeft') {
                prevImage();
            } else if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'Escape') {
                closeModal();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, prevImage, nextImage]);

    const toggleSection = () => {
        setIsOpen(!isOpen);
    };

    const hasValidHotels = hotels && hotels.length > 0 && hotels.some(hotel => hotel.name?.trim());
    const hasValidActivity = activity && (activity.name?.trim() || activity.image?.trim());
    const hasValidTransfer = transferFrom?.trim() || transferStop?.trim() || transferTo?.trim();
    const hasValidInclusions = inclusions && (inclusions.breakfast === 'Included' || inclusions.lunch === 'Included' || inclusions.dinner === 'Included');

    return (
        <div className="w-full max-w-screen-2xl mx-auto bg-custom-white rounded-lg shadow-lg mb-3 sm:mb-4">
            {/* Header */}
            <div className="flex justify-between items-center p-3 sm:p-4">
                <div className="flex items-center">
                    <span className="bg-custom-yellow text-custom-white text-xs sm:text-sm font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full mr-2">{day}</span>
                    <h2 className="text-lg sm:text-xl font-semibold text-custom-blue">{title}</h2>
                </div>
                <button onClick={toggleSection}>
                    <svg
                        className={`w-5 h-5 sm:w-6 sm:h-6 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div className="p-3 sm:p-4">
                    {/* Description */}
                    <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">{description}</p>

                    <hr className="bg-gray-300 h-0.5 mt-3 sm:mt-4 mb-3 sm:mb-4 border-none" />

                    {/* Shared Transfer */}
                    {hasValidTransfer && (
                        <div className="mb-3 sm:mb-4">
                            <div className="flex items-center mb-2">
                                <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 4h8M6 8h12M4 12h16M2 16h20" />
                                </svg>
                                <span className="text-custom-blue font-semibold text-sm sm:text-base">Shared Transfer</span>
                            </div>
                            <p className="text-gray-600 mb-2 text-sm sm:text-base">
                                {transferFrom?.trim() && transferTo?.trim() ? 'Transfer in Coach' : 'No transfer details available'}
                            </p>
                            {transferFrom?.trim() && (
                                <div className="relative mb-6 sm:mb-8 mt-4 sm:mt-6">
                                    <span
                                        className="absolute top-[-0.75rem] left-1 text-custom-blue text-xs sm:text-sm px-3 py-0 rounded-tl-md z-10"
                                        style={{ backgroundImage: `linear-gradient(to right, #FFC107, #FFFFFF)` }}
                                    >
                                        From
                                    </span>
                                    <div className="flex items-center shadow-md p-2 sm:p-3 mt-4 sm:mt-5 rounded">
                                        <svg
                                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-gray-600 text-sm sm:text-base">{transferFrom}</span>
                                    </div>
                                </div>
                            )}
                            {transferTo?.trim() && (
                                <div className="relative">
                                    <span
                                        className="absolute top-[-0.75rem] left-1 text-custom-blue text-xs sm:text-sm px-3 py-0 rounded-tl-md z-10"
                                        style={{ backgroundImage: `linear-gradient(to right, #FFC107, #FFFFFF)` }}
                                    >
                                        To
                                    </span>
                                    <div className="flex items-center shadow-md p-2 sm:p-3 mt-4 sm:mt-5 rounded">
                                        <svg
                                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12h18M12 3v18" />
                                        </svg>
                                        <span className="text-gray-600 text-sm sm:text-base">{transferTo}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {(hasValidHotels || checkInTime?.trim() || checkOutTime?.trim()) && (
                        <>
                            <hr className="bg-gray-300 h-0.5 mt-3 sm:mt-4 mb-3 sm:mb-4 border-none" />
                            {/* Check-In Section */}
                            <div className="mb-3 sm:mb-4">
                                {hasValidHotels && (
                                    <>
                                        <h5 className="text-gray-600 text-sm sm:text-base">Stay At</h5>
                                        <h3 className="text-base sm:text-lg font-semibold text-custom-blue mb-2">
                                            Check-In at {transferTo?.trim() || 'Deluxe Hotel'}
                                        </h3>
                                        {(checkInTime?.trim() || checkOutTime?.trim()) && (
                                            <div className="flex justify-between items-center p-2 bg-gray-100 text-gray-600 mb-2 rounded-lg relative">
                                                <div className="z-10">
                                                    <span className="text-xs sm:text-sm">Check In</span>
                                                    <p className="text-sm sm:text-base">{checkInTime?.trim() || 'N/A'}</p>
                                                </div>
                                                <div className="absolute left-1/2 top-5 sm:top-6 transform -translate-x-1/2 w-full flex justify-center items-center pointer-events-none">
                                                    <div className="w-4/6 border-t border-dotted border-gray-400 relative">
                                                        <span className="absolute top-[-0.75rem] left-1/2 transform -translate-x-1/2 bg-gray-200 px-2 text-xs sm:text-sm text-custom-blue font-semibold">
                                                            1N
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="z-10 text-right">
                                                    <span className="text-xs sm:text-sm">Check Out</span>
                                                    <p className="text-sm sm:text-base">{checkOutTime?.trim() || 'N/A'}</p>
                                                </div>
                                            </div>
                                        )}
                                        <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4">Stays will be allocated based on availability or similar category</p>

                                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-4">
                                            {hotels.map((hotel, index) => (
                                                (hotel.name?.trim() || hotel.rating || hotel.image?.trim()) && (
                                                    <div key={index} className="flex-1 relative overflow-hidden">
                                                        {hotel.image?.trim() && (
                                                            <>
                                                                <img
                                                                    src={hotel.image}
                                                                    alt={hotel.name?.trim() || 'Hotel'}
                                                                    className="w-full h-32 sm:h-40 object-cover rounded-lg mb-2 cursor-pointer transition-transform duration-700 hover:scale-105"
                                                                    onClick={() => openModal(index, hotels)}
                                                                    onError={(e) => (e.target.src = 'https://via.placeholder.com/150')}
                                                                />
                                                                <button
                                                                    onClick={() => openModal(index, hotels)}
                                                                    className="absolute bottom-2 right-2 flex items-center px-2 sm:px-3 py-1 bg-gray-800 bg-opacity-50 text-white text-xs sm:text-sm rounded-full hover:bg-opacity-75"
                                                                >
                                                                    View
                                                                </button>
                                                            </>
                                                        )}
                                                        {hotel.rating && (
                                                            <div className="flex items-center mb-1">
                                                                <svg
                                                                    className="w-3 h-3 sm:w-4 sm:h-4 text-custom-yellow mr-1"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                                </svg>
                                                                <span className="text-xs sm:text-sm">{hotel.rating}</span>
                                                            </div>
                                                        )}
                                                        {hotel.name?.trim() && (
                                                            <p className="text-custom-blue text-sm sm:text-base">{hotel.name}</p>
                                                        )}
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </>
                                )}

                                {hasValidInclusions && (
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4">
                                        <div>
                                            <p className="text-gray-600 text-xs sm:text-sm">1 Room – 2 Adult</p>
                                        </div>
                                        <div className="flex flex-wrap space-x-2 sm:space-x-4 mt-2 sm:mt-0">
                                            {['breakfast', 'lunch', 'dinner'].map((meal, index) => (
                                                inclusions[meal] === 'Included' && (
                                                    <div key={index} className="flex items-center">
                                                        <svg
                                                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-500"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                        <span className="text-gray-600 capitalize text-xs sm:text-sm">{meal}</span>
                                                        <svg
                                                            className="w-3 h-3 sm:w-4 sm:h-4 ml-1 text-green-500"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                        <span className="text-green-500 ml-1 text-xs sm:text-sm">Included</span>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {hasValidActivity && (
                                <>
                                    <hr className="bg-gray-300 h-0.5 mt-3 sm:mt-4 mb-3 sm:mb-4 border-none" />
                                    {/* Activity */}
                                    <div>
                                        <div className="flex items-center mb-2">
                                            <svg
                                                className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <span className="text-custom-blue font-semibold text-sm sm:text-base">Activity</span>
                                        </div>
                                        {activity.name?.trim() && (
                                            <h3 className="text-base sm:text-lg font-semibold text-custom-blue mb-2">{activity.name}</h3>
                                        )}
                                        {activity.image?.trim() && (
                                            <div className="relative">
                                                <img
                                                    src={activity.image}
                                                    alt={activity.name?.trim() || 'Activity'}
                                                    className="w-full h-48 sm:h-72 object-cover rounded-lg cursor-pointer transition-transform duration-700 hover:scale-105"
                                                    onClick={() => openModal(hotels.filter(h => h.image?.trim()).length, hotels)}
                                                    onError={(e) => (e.target.src = 'https://via.placeholder.com/300')}
                                                />
                                                <button
                                                    onClick={() => openModal(hotels.filter(h => h.image?.trim()).length, hotels)}
                                                    className="absolute bottom-2 left-2 flex items-center px-2 sm:px-3 py-1 bg-gray-800 bg-opacity-50 text-white text-xs sm:text-sm rounded-full hover:bg-opacity-75"
                                                >
                                                    View
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Image Viewer Modal */}
            {isModalOpen && modalImages.length > 0 && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-50">
                    <div className="relative w-full max-w-7xl h-[90vh] sm:h-[100vh] bg-black flex flex-col">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 sm:top-8 sm:right-8 text-white hover:text-gray-300 z-50"
                        >
                            <svg
                                className="w-6 h-6 sm:w-8 sm:h-8"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                ></path>
                            </svg>
                        </button>
                        <div className="relative flex-1 flex items-center justify-center">
                            <img
                                src={modalImages[currentImageIndex].url}
                                alt={modalImages[currentImageIndex].alt}
                                className="w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg"
                                onError={(e) => (e.target.src = 'https://via.placeholder.com/300')}
                            />
                            <button
                                onClick={prevImage}
                                className="absolute left-2 sm:left-8 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                            >
                                <svg
                                    className="w-6 h-6 sm:w-8 sm:h-8"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M15 19l-7-7 7-7"
                                    ></path>
                                </svg>
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-2 sm:right-8 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                            >
                                <svg
                                    className="w-6 h-6 sm:w-8 sm:h-8"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 5l7 7-7 7"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                        <div className="text-center text-white mt-2 mb-4 text-sm sm:text-base">
                            {currentImageIndex + 1} / {modalImages.length}
                        </div>
                        <div className="flex justify-center items-center gap-2 overflow-x-auto pb-4 px-2">
                            {modalImages.map((item, index) => (
                                <img
                                    key={index}
                                    src={item.url}
                                    alt={item.alt}
                                    className={`w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg cursor-pointer ${currentImageIndex === index ? 'border-2 border-white' : ''}`}
                                    onClick={() => setCurrentImageIndex(index)}
                                    onError={(e) => (e.target.src = 'https://via.placeholder.com/150')}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItineraryAccordion;